import { Observable } from 'rxjs/Rx';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ModalDirective } from 'ng2-bootstrap/modal';
import { FormlyFieldConfig } from 'ng-formly/src/core/components/formly.field.config';
import { Input, ViewChild } from '@angular/core';
import { Dataelement, DataelementApi, Entity, EntityApi, LoopBackFilter } from '../sdk';
import { isArray } from 'rxjs/util/isArray';
import * as _ from 'lodash';
import * as cryptojs from 'crypto-js';

/**
 * This function coerces a string into a string literal type.
 * Using tagged union types in TypeScript 2.0, this enables
 * powerful typechecking of our reducers.
 *
 * Since every action label passes through this function it
 * is a good place to ensure all of our action labels
 * are unique.
 */

let typeCache: { [label: string]: boolean } = {};
export function type<T>(label: T | ''): T {
  if (typeCache[<string>label]) {
    throw new Error(`Action type '${label}' is not unique'`);
  }

  typeCache[<string>label] = true;

  return <T>label;
}

export function isNotEmpty(array): boolean {
  return (array && isArray(array) && array.length > 0);
}

export function isEmpty(array): boolean {
  return (!array || !isArray(array) || array.length == 0);
}

export function tolowercase(value: string) {
  if (value) {
    return value.trim().toLowerCase();
  }
  return value;
}

export function touppercase(value: string) {
  if (value) {
    return value.trim().toUpperCase();
  }
  return value;
}

export function trim(value: string) {
  if (value && typeof value.trim == "function") {
    return value.trim();
  }
  return value;
}

export function isNotBlank(value: string): boolean {
  return (value && trim(value) != '');
}

export function isBlank(value: string): boolean {
  return (!value || trim(value) == '');
}

export function equalsIgnoreCase(str1: string, str2: string) {
  if (str1 == str2) {
    return true;
  } else {
    return tolowercase(str1) === tolowercase(str2);
  }
}

export function removeAllWhiteSpace(value: string) {
  if (value) {
    return value.trim().replace(/ /g, '');
  }
  return value;
}

export function getFieldFromArray(fields: Array<any>, name: string, fieldname?: string): any {
  if (isNotEmpty(fields) && name) {
    return fields.find(f => {
      if (fieldname) {
        return f[fieldname] === name;
      } else if (typeof f === 'object' && f.hasOwnProperty('name')) {
        return f.name === name;
      }
      return f === name;
    });
  }
  return null;
}

export abstract class AbstractWidgetComponent {
  public filter_fields: Array<FilterField> = [];
  public selected_filters: Array<FilterField> = [];
  public record = {};
  protected appid: string;
  protected user: any;
  @Input() entityrecordid: any;
  @Input() entityid: any;
  @Input() autorefresh = false;
  @Input('filters') filters_from_parent: Array<any> = [];
  internal_filter: Array<any> = [];
  protected entity: any;
  protected _timer;
  public options;
  protected _dataelementid;
  public vm_fields = [];
  @ViewChild('filterModal') public filterModal: ModalDirective;
  public filterform: FormGroup;
  public filterFormFields: Array<FormlyFieldConfig> = [];

  constructor(protected _dataelementApi: DataelementApi, protected fb: FormBuilder, protected _loopBackAuth, protected _entityApi: EntityApi) {
    this.vm_fields = [this.constructFilterFields()];
    this.user = this._loopBackAuth.getCurrentUserData();
    this.appid = this.user.appId;
  }

  constructFilterFields() {
    return {
      type: 'repeatedsection',
      key: 'filter_fields',
      fieldArray: {
        className: 'row',
        fieldGroup: [
          {
            className: 'col-md-0',
            type: 'input-field',
            key: 'name',
            hideExpression: true,
            templateOptions: {
              type: 'text',
              label: 'Name',
              required: true,
              disabled: true
            },
          },
          {
            className: 'col-md-3',
            type: 'input-field',
            key: 'label',
            templateOptions: {
              type: 'text',
              label: 'Filter Field',
              required: true,
              disabled: true
            },
          },
          {
            className: 'col-md-0',
            type: 'input-field',
            key: 'fieldtype',
            hideExpression: true,
            templateOptions: {
              type: 'text',
              label: 'Field Type',
              required: true
            }
          },
          {
            className: 'col-md-3',
            type: 'input-field',
            key: 'inputtype',
            hideExpression: true,
            templateOptions: {
              type: 'text',
              label: 'Input Type',
              required: true
            }
          },
          {
            className: 'col-md-0',
            type: 'input-field',
            key: 'relateduid',
            hideExpression: true,
            templateOptions: {
              type: 'text',
              label: 'Related Unique Id for lookup',
              required: false
            }
          },
          {
            className: 'col-md-3',
            type: 'select-component',
            key: 'filtertype',
            templateOptions: {
              label: 'Filter Type',
              options: [],
              required: true
            },
            lifecycle: {
              onInit: (form, model, field) => {
                let opts = [];
                switch (field['fieldtype']) {
                  case 'text' || 'string' || 'email' || 'url':
                    opts.push({ label: 'Equals', value: 'equals' });
                    opts.push({ label: 'Contains', value: 'contains' });
                    break;
                  case 'textarea':
                    opts.push({ label: 'Contains', value: 'contains' });
                    break;
                  case 'number' || 'date' || 'dateandtime' || 'currency' || 'time' || 'amount' || 'percent':
                    opts.push({ label: 'Equals', value: 'equals' });
                    opts.push({ label: 'Greater than', value: 'gt' });
                    opts.push({ label: 'Greater than Or Equal to', value: 'gte' });
                    opts.push({ label: 'Lesser than', value: 'lt' });
                    opts.push({ label: 'Lesser than Or Equal to', value: 'lte' });
                    break;
                  default:
                    opts.push({ label: 'Equals', value: 'equals' });
                    break;
                }
                model.templateOptions.options = opts;
              },
            }
          },
          {
            className: 'col-md-4',
            type: 'input-field',
            key: 'selected_v',
            lookupfield: 'model.relateduid',
            lookupfieldname: 'name',
            hideExpression: '(model.inputtype && model.inputtype !== "input")',
            templateOptions: {
              type: 'text',
              label: 'Value',
              required: false,
              placeholder: 'Select a value'
            },
            expressionProperties: {
              'templateOptions.required': 'model.inputtype === "input" ? true : false'
            }
          },
          {
            className: 'col-md-4',
            type: 'select-component',
            key: 'selected_v',
            lookupfield: 'model.relateduid',
            lookupfieldname: 'name',
            hideExpression: '(!model.inputtype || model.inputtype !== "lookup")',
            templateOptions: {
              type: 'entityrecord',
              label: 'Value',
              required: false,
              placeholder: 'Select a value'
            },
            expressionProperties: {
              'lookupfield': 'model.relateduid',
              'templateOptions.required': 'model.inputtype === "lookup" ? true : false'
            }
          },
          {
            className: 'col-md-4',
            type: 'picklist-lookup',
            key: 'selected_v',
            lookupfield: 'model.relateduid',
            hideExpression: '(!model.inputtype || model.inputtype !== "picklist")',
            templateOptions: {
              label: 'Value',
              required: false,
              placeholder: 'Select a value'
            },
            expressionProperties: {
              'lookupfield': 'model.relateduid',
              'templateOptions.required': 'model.inputtype === "picklist" ? true : false'
            }
          }
        ]
      }
    };
  }

  loadWidgetData() {
    this.getWidgetData();
    if (this.autorefresh && !this._timer) {
      this._timer = setInterval(() => this.setChartConfig(), 3000);
    } else if (!this.autorefresh && this._timer) {
      clearInterval(this._timer);
    }
  }

  getWidgetData() {
    this.widgetData().subscribe(result => {
      if (result) {
        this.filter_fields = _.cloneDeep(result['filterfields']);
        this.options = this.createChartConfig(result);
      }
    }, err => console.error(err));
  }

  widgetData(): Observable<Dataelement> {
    if (this.entityid && this.entityrecordid) {
      return this.getEntity().flatMap(entity => this.getDataElement(entity));
    } else {
      return this.getDataElement();
    }
  }

  getDataElement(entity?): Observable<Dataelement> {
    let filter: LoopBackFilter = {};
    filter['data'] = true;
    if (isNotEmpty(this.selected_filters)) {
      filter.where = filter.where || {};
      filter = this.applyFilter(filter, this.selected_filters);
      let fs = this.selected_filters.filter(sf => removeAllWhiteSpace(tolowercase(sf.filtertype)).startsWith('equals'));
      if (isNotEmpty(fs)) {
        fs.forEach(f => filter[f.name] = f.selected_v);
      }
    }
    if (isNotEmpty(this.filters_from_parent)) {
      filter.where = filter.where || {};
      filter = this.applyFilter(filter, this.filters_from_parent);
      let fs = this.filters_from_parent.filter(sf => removeAllWhiteSpace(tolowercase(sf.filtertype)).startsWith('equals'));
      if (isNotEmpty(fs)) {
        fs.forEach(f => filter[f.name] = f.selected_v);
      }
    }
    if (isNotEmpty(this.internal_filter)) {
      filter.where = filter.where || {};
      filter = this.applyFilter(filter, this.internal_filter);
      let fs = this.internal_filter.filter(sf => removeAllWhiteSpace(tolowercase(sf.filtertype)).startsWith('equals'));
      if (isNotEmpty(fs)) {
        fs.forEach(f => filter[f.name] = f.selected_v);
      }
    }
    let e = entity || this.entity;
    if (e) {
      filter[e.name] = this.entityrecordid;
    }
    // console.log('filter:-', JSON.stringify(filter), ' for dataelement:-', this._dataelementid);
    return this._dataelementApi.findById(this._dataelementid, filter);
  }

  getEntity(): Observable<any> {
    if (this.entity) {
      return Observable.of(this.entity);
    } else {
      return this._entityApi.findById(this.entityid)
        .map(result => { this.entity = Object.assign({}, result); return result; });
    }
  }

  abstract createChartConfig(result): any;

  setChartConfig() {
    if (this._dataelementid) { this.loadWidgetData(); }
  }

  public onFilterChange(selecedField) {
    let sff = this.selected_filters.find(sff => sff.name === selecedField.name);
    if (sff) {
      sff.fieldtype = selecedField.fieldtype;
      sff.selected_v = selecedField.selected_v;
    } else {
      let selectedFilterField = new FilterField(selecedField.name);
      selectedFilterField.fieldtype = selecedField.fieldtype;
      selectedFilterField.selected_v = selecedField.selected_v;
      this.selected_filters.push(selecedField);
    }
    this.getWidgetData();
  }

  public onFilterRemoval(selectedFilter) {
    let index = this.selected_filters.indexOf(selectedFilter);
    if (index !== -1) {
      this.selected_filters.splice(index, 1);
      this.getWidgetData();
    }
  }

  showFilterModal() {
    if (!this.filterform) {
      this.filterform = this.fb.group({});
    }
    this.filterform.reset();
    this.record = {};
    this.vm_fields = [this.constructFilterFields()];
    // always take new copy of fields that can be filtered
    this.record['filter_fields'] = _.cloneDeep(this.filter_fields);
    if (!isNotEmpty(this.selected_filters)) { // over ride the filed values with already client selected filters & specs
      this.selected_filters.forEach(sff => {
        let ff = this.record['filter_fields'].find(ff => ff.name === sff.name);
        if (ff) { ff = Object.assign({}, sff); }
      });
    }
    this.filterModal.show();
  }

  applyFormFilter(record) {
    this.selected_filters = [];
    record['filter_fields'].forEach(rff => {
      if (rff && rff['filtertype'] && rff['selected_v']) {
        this.selected_filters.push(_.cloneDeep(rff));
      }
    });
    this.loadWidgetData();
    this.hideFilterModal();
  }

  resetFormField() {
    this.record['filter_fields'] = _.cloneDeep(this.filter_fields);
    this.filterform.reset(this.record);
  }

  hideFilterModal() {
    this.record = {};
    if (this.filterform && this.filterform['subscriptions'])
      this.filterform['subscriptions'].forEach(s => { s.unsubscribe(); });
    if (this.filterform)
      this.filterform = this.fb.group({});
    this.filterModal.hide();
  }

  applyFilter(filter: LoopBackFilter, filterfields: Array<any>) {
    filterfields.forEach(sff => {
      if (sff.filtertype && sff.selected_v) {
        let ft = removeAllWhiteSpace(tolowercase(sff.filtertype));
        if (ft.startsWith('equals') || ft.startsWith('eq')) {
          filter.where[sff.name] = sff.selected_v;
        } else if (ft.startsWith('contains')) {
          filter.where[sff.name] = { 'like': '%' + sff.selected_v + '%' };
        } else if (ft.startsWith('greaterthanorequal') || ft === 'gte') {
          filter.where[sff.name] = { 'gte': sff.selected_v };
        } else if (ft.startsWith('greaterthan') || ft === 'gt') {
          filter.where[sff.name] = { 'gt': sff.selected_v };
        } else if (ft.startsWith('lessthanorequal') || ft === 'lte') {
          filter.where[sff.name] = { 'lte': sff.selected_v };
        } else if (ft.startsWith('lessthan') || ft === 'lt') {
          filter.where[sff.name] = { 'lt': sff.selected_v };
        } else if (ft === 'between') {
          filter.where[sff.name] = { 'between': sff.selected_v };
        }
      }
    });
    return filter;
  }
}

export class FilterField {
  constructor(public name: string) {
  }
  public label: string;
  public fieldtype: string;
  public filtertype: string;
  public inputtype: string;
  public relateduid: string;
  public values: Array<string> = [];
  public selected_v: string;
}

export class valueLabel {
  public value: any;
  public label: any;

  constructor(label, value) {
    this.label = label;
    this.value = value;
  }
}

export class dateObject {
  public year: any;
  public month: any;
  public day: any;

  constructor(year, month, day) {
    this.year = year;
    this.month = month;
    this.day = day;
  }
}

const enc_pwd = 'f02b4728974ff88e9c6114b398d4e43d3c8b6a189a92abb2ea88bcf4fc905898';
const password = cryptojs.enc.Hex.parse(enc_pwd);


export function encrypt(text) {
  const iv = cryptojs.lib.WordArray.random(16);
  const encrypted = cryptojs.AES.encrypt(text, password, {
      iv: iv,
      mode: cryptojs.mode.CTR,
      padding: cryptojs.pad.NoPadding
  });
  return encrypted.ciphertext.toString() + '.' + encrypted.iv.toString();
}


// WEBPACK FOOTER //
// ./src/app/shared/utils/util.ts